import { useEffect, useState, useCallback } from "react";
import {
  FaPlus,
  FaEdit,
  FaFolder,
  FaFolderOpen,
  FaBox,
  FaTimes,
  FaCheck,
} from "react-icons/fa";
import { useApi } from "../context/useApi";

const API_BASE =
  "https://admin-backend-fbuuh6fo3q-el.a.run.app/productcategory";

const ProductCategoryPanel = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, root, child
const { apiFetch } = useApi();
  const [formData, setFormData] = useState({
    category_name: "",
    parent_category: "none",
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

const fetchCategories = useCallback(async () => {
  setLoading(true);
  try {
    const res = await apiFetch(`${API_BASE}/get-categories`);
    if (!res) return;

    const data = await res.json();
    if (res.ok) {
      setCategories(data.data || []);
    } else {
      showToast(data.error || "Failed to fetch categories", "error");
    }
  } catch (err) {
    console.error("Error fetching categories:", err);
    showToast("Something went wrong while fetching categories", "error");
  } finally {
    setLoading(false);
  }
}, [apiFetch]);

useEffect(() => {
  fetchCategories();
}, [fetchCategories]);

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category.id);
      setFormData({
        category_name: category.category_name,
        parent_category: category.parent_category || "none",
      });
    } else {
      setEditingCategory(null);
      setFormData({
        category_name: "",
        parent_category: "none",
      });
    }
    setShowModal(true);
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!formData.category_name) {
    showToast("Category name is required", "error");
    return;
  }
  setIsSaving(true);
  try {
    let res;
    if (editingCategory) {
      res = await apiFetch(`${API_BASE}/update-category/${editingCategory}`, {
        method: "PUT",
        body: JSON.stringify(formData),
      });
    } else {
      res = await apiFetch(`${API_BASE}/add-category`, {
        method: "POST",
        body: JSON.stringify(formData),
      });
    }

    if (!res) return;

    const data = await res.json();
    if (res.ok) {
      showToast(
        editingCategory
          ? "Category updated successfully!"
          : "Category added successfully!",
        "success"
      );
      await fetchCategories();
      setShowModal(false);
    } else {
      showToast(data.error || "Failed to save category", "error");
    }
  } catch (err) {
    console.error("Error saving category:", err);
    showToast("Failed to save category", "error");
  } finally {
    setIsSaving(false);
  }
};

  // Filter categories
  const filteredCategories = categories.filter((cat) => {
    const matchesSearch = cat.category_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === "all" ||
      (filterType === "root" && cat.parent_category === "none") ||
      (filterType === "child" && cat.parent_category !== "none");

    return matchesSearch && matchesType;
  });

  // Group categories by hierarchy
  const rootCategories = filteredCategories.filter(
    (cat) => cat.parent_category === "none"
  );
  const childCategories = filteredCategories.filter(
    (cat) => cat.parent_category !== "none"
  );

  // Get children for a parent
  const getChildren = (parentName) => {
    return childCategories.filter((cat) => cat.parent_category === parentName);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Product Categories
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Organize and manage product categories • {categories.length} total
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm font-medium"
          >
            <FaPlus size={12} />
            Add Category
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="root">Root Categories</option>
            <option value="child">Sub Categories</option>
          </select>
        </div>
      </div>

      {/* Categories Display */}
{loading ? (
  <div className="space-y-3">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="border-2 border-gray-200 rounded-lg p-4 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border-2 border-gray-300 rounded"></div>
            <div className="space-y-2">
              <div className="h-5 w-32 border border-gray-300 rounded"></div>
              <div className="h-3 w-48 border border-gray-300 rounded"></div>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="h-8 w-8 border border-gray-300 rounded"></div>
            <div className="h-8 w-8 border border-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
) : filteredCategories.length === 0 ? (
  <div className="border-2 border-dashed border-gray-300 rounded-lg p-16 text-center">
    <div className="inline-block p-4 border-2 border-gray-300 rounded-full mb-4">
      <FaFolder className="text-gray-400" size={48} />
    </div>
    <p className="text-gray-600 font-medium mb-1">No categories found</p>
    <p className="text-sm text-gray-500">
      {searchTerm
        ? "Try a different search term"
        : "Start by adding a new category"}
    </p>
  </div>
) : (
        <div className="space-y-6">
          {/* Hierarchical View */}
          {rootCategories.map((rootCat) => {
            const children = getChildren(rootCat.category_name);

            return (
              <div
                key={rootCat.id}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Root Category */}
                <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                  <div className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                        <FaFolderOpen className="text-white" size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {rootCat.category_name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                          Root Category • {children.length} subcategories
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <FaBox className="text-gray-400" size={14} />
                          <span className="text-2xl font-bold text-gray-900">
                            {rootCat.num_products}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">products</p>
                      </div>
                      <button
                        onClick={() => handleOpenModal(rootCat)}
                        className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                      >
                        <FaEdit size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Child Categories */}
                {children.length > 0 && (
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {children.map((child) => (
                        <div
                          key={child.id}
                          className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                <FaFolder className="text-gray-600" size={14} />
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {child.category_name}
                                </h4>
                                <p className="text-xs text-gray-500">
                                  Subcategory
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleOpenModal(child)}
                              className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                            >
                              <FaEdit size={12} />
                            </button>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <span className="text-xs text-gray-500">
                              Products
                            </span>
                            <span className="text-sm font-semibold text-gray-900">
                              {child.num_products}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Orphan child categories */}
          {filterType !== "root" &&
            childCategories.filter(
              (cat) =>
                !rootCategories.some(
                  (root) => root.category_name === cat.parent_category
                )
            ).length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  Other Subcategories
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {childCategories
                    .filter(
                      (cat) =>
                        !rootCategories.some(
                          (root) => root.category_name === cat.parent_category
                        )
                    )
                    .map((child) => (
                      <div
                        key={child.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                              <FaFolder className="text-gray-600" size={14} />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">
                                {child.category_name}
                              </h4>
                              <p className="text-xs text-gray-500">
                                Parent: {child.parent_category}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleOpenModal(child)}
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                          >
                            <FaEdit size={12} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <span className="text-xs text-gray-500">
                            Products
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {child.num_products}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCategory ? "Edit Category" : "Add New Category"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Category Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={formData.category_name}
                  onChange={(e) =>
                    setFormData({ ...formData, category_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Parent Category
                </label>
                <select
                  value={formData.parent_category}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      parent_category: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="none">None (Root Category)</option>
                  {categories
                    .filter(
                      (cat) =>
                        cat.parent_category === "none" &&
                        cat.id !== editingCategory
                    )
                    .map((cat) => (
                      <option key={cat.id} value={cat.category_name}>
                        {cat.category_name}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a parent category to create a subcategory
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                >
                  <FaCheck size={12} />
                  {isSaving
                    ? editingCategory
                      ? "Updating..."
                      : "Adding..."
                    : editingCategory
                    ? "Update Category"
                    : "Add Category"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === "success" ? "bg-gray-900" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ProductCategoryPanel;


